import { Area, Size } from '../../../../../shared/helpers/layout-manager/types';
import logError from '../../../../../shared/helpers/log-error';

export const drawElement = (
    ctx: CanvasRenderingContext2D,
    source: CanvasImageSource,
    sourceArea: Area,
    targetArea: Area
) => {
    ctx.drawImage(
        source,
        sourceArea.x,
        sourceArea.y,
        sourceArea.width,
        sourceArea.height,
        targetArea.x,
        targetArea.y,
        targetArea.width,
        targetArea.height
    );
};

export type DisplayMode = 'cover' | 'contain';
export type Align = 'start' | 'center' | 'end';

export interface DrawMediaOptions {
    mode: DisplayMode;
    isMirror?: boolean;
    horizontalAlign?: Align;
    verticalAlign?: Align;
}

const alignDimension = (innerLength: number, outerLength: number, align: Align) => {
    switch (align) {
        case 'start':
            return 0;
        case 'center':
            return Math.floor((outerLength - innerLength) / 2);
        case 'end':
            return outerLength - innerLength;
    }
};

function getMediaSize(element: HTMLVideoElement | HTMLImageElement | OffscreenCanvas): Size {
    if (element instanceof HTMLImageElement) {
        return { width: element.naturalWidth, height: element.naturalHeight };
    }

    if (element instanceof HTMLVideoElement) {
        return { width: element.videoWidth, height: element.videoHeight };
    }

    return { width: element.width, height: element.height };
}

export const drawMedia = (
    ctx: CanvasRenderingContext2D,
    area: Area,
    element: HTMLVideoElement | HTMLImageElement | OffscreenCanvas | undefined,
    { mode, isMirror = false, horizontalAlign = 'center', verticalAlign = 'center' }: DrawMediaOptions
): void => {
    if (!element) {
        return;
    }

    const mediaSize = getMediaSize(element);
    const sourceRatio = mediaSize.width / mediaSize.height;
    const targetRatio = area.width / area.height;

    let sourceSize: Size = mediaSize;
    let targetSize: Size = area;

    if (mode === 'cover') {
        // COVER
        if (sourceRatio > targetRatio) {
            // Горизонтальные видео
            const cropWidth = mediaSize.height * targetRatio;
            sourceSize = { width: cropWidth, height: mediaSize.height };
        } else {
            // Вертикальные видео
            // Вписываем и центрируем по горизонтали
            const cropWidth = targetSize.height * sourceRatio;
            targetSize = { width: cropWidth, height: targetSize.height };
        }
    } else {
        // CONTAIN
        if (sourceRatio > targetRatio) {
            const cropHeight = area.width / sourceRatio;
            targetSize = { width: area.width, height: cropHeight };
        } else {
            const cropWidth = area.height * sourceRatio;
            targetSize = { width: cropWidth, height: area.height };
        }
    }

    const sourceArea = {
        x: alignDimension(sourceSize.width, mediaSize.width, horizontalAlign),
        y: alignDimension(sourceSize.height, mediaSize.height, verticalAlign),
        ...sourceSize,
    };

    const targetArea = {
        x: area.x + alignDimension(targetSize.width, area.width, horizontalAlign),
        y: area.y + alignDimension(targetSize.height, area.height, verticalAlign),
        ...targetSize,
    };

    if (isMirror) {
        ctx.save();

        // Горизонтально отражаем
        ctx.scale(-1, 1);

        drawElement(ctx, element, sourceArea, {
            x: -targetArea.x,
            y: targetArea.y,
            width: -targetArea.width,
            height: targetArea.height,
        });

        ctx.restore();
    } else {
        drawElement(ctx, element, sourceArea, targetArea);
    }
};

let wasDrawMediaErrorLogged = false;

export const safeDrawMedia = (
    ctx: CanvasRenderingContext2D,
    area: Area,
    element: HTMLVideoElement | HTMLImageElement | OffscreenCanvas | undefined,
    options: DrawMediaOptions
): void => {
    try {
        drawMedia(ctx, area, element, options);
    } catch (error) {
        // Залогируем только первую ошибку, иначе на каждый кадр - слишком жестко
        if (!wasDrawMediaErrorLogged) {
            wasDrawMediaErrorLogged = true;
            logError({ block: 'recording', method: 'drawMedia' }, error as Error);
        }

        // eslint-disable-next-line no-console
        console.error(error);
    }
};
